/* eslint-disable no-shadow */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { FiPlay, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { createGlobalStyle } from 'styled-components';

import { documentMouseUp } from '../../shared/actions';
import {
   setInstruments,
   setPattern,
   setView,
   toggleUserPlaying,
   toggleReferencePlaying,
   setTempo,
   toggleLoopModePlaying,
   toggleLoopModeReference,
   setSwing,
} from './actions';
import {
   sequencerInstrumentControlTypes,
   sequencerViewTypes,
} from './constants';
import { StepSequencer } from './stepSequencer/StepSequencer';
import { SequencerStyles } from './SequencerStyles';
import { PianoRoll } from './pianoRoll/PianoRoll';
import { Mixer } from './mixer/Mixer';
import { Effect } from './mixer/Effect';
import { ControlsWrapper } from '../../components/ControlsWrapper';
import { Tempo } from './Tempo';
import { Knob } from '../../components/Knob';
import { ControllerStyles } from '../../components/ControllerStyles';
import { controllerValuesEqual } from '../../util/controllerValuesEqual';
import { getHighlightedClassName } from '../../util/getHighlightedClassName';
import { Button } from '../../components/Button';
import { TrackRouting } from './mixer/TrackRouting';
import { TrackEffectList } from './mixer/TrackEffectList';
import { FullMixer } from './mixer/FullMixer';

const SequencerDefaultCssVariableOverrides = createGlobalStyle`
   :root {
      @media  (min-height: 500px) {
         --exercise-bottom-padding: 1.5em;
      }

      @media  (min-height: 750px) {
         --exercise-bottom-padding: 2.5em;
      }
   }
`;

const mapStateToProps = ({ activeInterface }) => ({
   state: activeInterface,
});

const mapDispatchToProps = {
   setInstruments,
   toggleLoopModePlaying,
   toggleLoopModeReference,
   toggleUserPlaying,
   toggleReferencePlaying,
   documentMouseUp,
   setPattern,
   setTempo,
   setSwing,
   setView,
};

const SequencerInterface = connect(
   mapStateToProps,
   mapDispatchToProps
)(
   ({
      state,
      setInstruments,
      toggleLoopModePlaying,
      toggleLoopModeReference,
      toggleUserPlaying,
      toggleReferencePlaying,
      documentMouseUp,
      setTempo,
      setSwing,
   }) => {
      useEffect(() => {
         const handler = (e) => {
            documentMouseUp(e.button);
         };
         document.addEventListener('mouseup', handler);
         return () => document.removeEventListener('mouseup', handler);
      }, [documentMouseUp]);

      return (
         <SequencerStyles
            id="sequencer-interface"
            className={classnames({
               'compare-answer': state.compareAnswer,
               'reference-playing': state.referencePlaying,
               'selected-playing': state.userPlaying,
               'reference-toggled': state.loopModeReference,
               'piano-roll-or-step-sequencer': [
                  sequencerViewTypes.pianoRoll,
                  sequencerViewTypes.stepSequencer,
               ].includes(state.view.viewType),
            })}
         >
            <SequencerDefaultCssVariableOverrides />
            <div>
               <ControlsWrapper className="controls-wrapper">
                  {!state.loopMode && (
                     <div className="play-buttons">
                        {state.hasReference && (
                           <Button
                              type="button"
                              onClick={toggleReferencePlaying}
                              className={classnames(
                                 'button-icon icon-with-text',
                                 {
                                    [getHighlightedClassName(
                                       state.highlightedControls.playReference
                                    )]:
                                       !!state.highlightedControls
                                          .playReference,
                                 }
                              )}
                           >
                              {state.referencePlaying ? (
                                 <svg
                                    stroke="currentColor"
                                    fill="none"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    height="24"
                                    width="24"
                                    xmlns="http://www.w3.org/2000/svg"
                                 >
                                    <rect x="4" y="4" width="16" height="16" />
                                 </svg>
                              ) : (
                                 <FiPlay size="24" />
                              )}{' '}
                              <span className="play-button-text">
                                 Reference
                              </span>
                           </Button>
                        )}
                        <Button
                           type="button"
                           onClick={toggleUserPlaying}
                           className={classnames('button-icon icon-with-text', {
                              [getHighlightedClassName(
                                 state.highlightedControls.playUser
                              )]: !!state.highlightedControls.playUser,
                           })}
                        >
                           {state.userPlaying ? (
                              <svg
                                 stroke="currentColor"
                                 fill="none"
                                 strokeWidth="2"
                                 viewBox="0 0 24 24"
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 height="24"
                                 width="24"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <rect x="4" y="4" width="16" height="16" />
                              </svg>
                           ) : (
                              <FiPlay size="24" />
                           )}{' '}
                           <span className="play-button-text">Pattern</span>
                        </Button>
                     </div>
                  )}
                  {state.loopMode && (
                     <div>
                        <Button
                           type="button"
                           onClick={toggleLoopModePlaying}
                           className={classnames('button-icon', {
                              [getHighlightedClassName(
                                 state.highlightedControls.playLoop
                              )]: !!state.highlightedControls.playLoop,
                           })}
                        >
                           {state.loopModePlaying ? (
                              <svg
                                 stroke="currentColor"
                                 fill="none"
                                 strokeWidth="2"
                                 viewBox="0 0 24 24"
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 height="24"
                                 width="24"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <rect x="4" y="4" width="16" height="16" />
                              </svg>
                           ) : (
                              <FiPlay size="24" />
                           )}
                        </Button>

                        {state.hasReference && (
                           <Button
                              type="button"
                              onClick={toggleLoopModeReference}
                              className={classnames('reference-toggle-button', {
                                 'reference-toggle-button-playing':
                                    state.loopModePlaying,
                                 'reference-toggle-button-reference':
                                    state.loopModeReference,
                                 [getHighlightedClassName(
                                    state.highlightedControls.toggleReference
                                 )]:
                                    !!state.highlightedControls.toggleReference,
                              })}
                           >
                              <span className="reference-toggle-text-reference">
                                 Reference
                              </span>
                              {state.loopModeReference ? (
                                 <FiToggleLeft size="24" />
                              ) : (
                                 <FiToggleRight size="24" />
                              )}
                              <span className="reference-toggle-text-yours">
                                 Yours
                              </span>
                           </Button>
                        )}
                     </div>
                  )}
                  <div className="swing-tempo-buttons">
                     {state.swingConfig.display && (
                        <svg
                           className="swing-control"
                           height="44"
                           width="44"
                           onContextMenu={(e) => e.preventDefault()}
                        >
                           <ControllerStyles
                              compareAnswer={state.compareAnswer}
                           >
                              <text
                                 y="10"
                                 x="3"
                                 style={{
                                    opacity:
                                       state.suggestedValues[`swing`] == null
                                          ? 1
                                          : 0.5,
                                 }}
                              >
                                 Swing
                              </text>
                              <Knob
                                 x={7}
                                 y={14}
                                 diameter={28}
                                 value={
                                    state.compareAnswer &&
                                    state.referencePlaying
                                       ? state.referenceSwing
                                       : state.swing
                                 }
                                 onChange={(swing) => setSwing(swing)}
                                 incrementCount={
                                    state.swingConfig.incrementCount
                                 }
                                 disabled={state.compareAnswer}
                                 tested
                                 highlighted={
                                    state.highlightedControls['swing']
                                 }
                                 correct={controllerValuesEqual(
                                    state.swing,
                                    state.referenceSwing
                                 )}
                                 suggestedValue={state.suggestedValues[`swing`]}
                              />
                           </ControllerStyles>
                        </svg>
                     )}
                     {state.tempoConfig.display && (
                        <Tempo
                           min={state.tempoConfig.range[0]}
                           max={state.tempoConfig.range[1]}
                           step={state.tempoConfig.increment}
                           onChange={(newTempo) => setTempo(newTempo)}
                           tempo={state.tempo}
                           referenceTempo={state.referenceTempo}
                           compareAnswer={state.compareAnswer}
                           referencePlaying={state.referencePlaying}
                           highlightedControls={state.highlightedControls}
                        />
                     )}
                  </div>
               </ControlsWrapper>
               <div className="sequencer-wrapper">
                  {state.view.viewType === sequencerViewTypes.stepSequencer && (
                     <StepSequencer />
                  )}
                  {state.view.viewType === sequencerViewTypes.pianoRoll && (
                     <PianoRoll compareAnswer={state.compareAnswer} />
                  )}
                  {state.view.viewType === sequencerViewTypes.mixer && (
                     <Mixer />
                  )}
                  {state.view.viewType === sequencerViewTypes.fullMixer && (
                     <FullMixer />
                  )}
                  {state.view.viewType ===
                     sequencerViewTypes.mixerEffectList && <TrackEffectList />}
                  {state.view.viewType === sequencerViewTypes.mixerRouting && (
                     <TrackRouting />
                  )}
                  {state.view.viewType === sequencerViewTypes.mixerEffect && (
                     <Effect />
                  )}
               </div>
            </div>
         </SequencerStyles>
      );
   }
);

export { SequencerInterface };
